import React, { useState } from 'react';
import { authHeaders } from '../../services/auth';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Database, PlayCircle, CheckCircle2, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ResultRow = ({ label, value, tone = 'slate' }) => {
  const colors = {
    slate: 'text-slate-900',
    green: 'text-emerald-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
    blue: 'text-blue-700',
  };
  return (
    <div className="flex justify-between items-baseline py-1 border-b border-slate-100 last:border-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-sm font-semibold ${colors[tone]}`}>{value}</span>
    </div>
  );
};

const run = async (dryRun) => {
  const res = await fetch(`${API}/api/vehicles/allowlist/backfill-user-fields?dry_run=${dryRun}`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.detail || 'Backfill failed');
  return j;
};

export const SAAirtableBackfillPanel = () => {
  const [dryResult, setDryResult] = useState(null);
  const [liveResult, setLiveResult] = useState(null);
  const [dryRunning, setDryRunning] = useState(false);
  const [liveRunning, setLiveRunning] = useState(false);

  const doDryRun = async () => {
    setDryRunning(true);
    setDryResult(null);
    setLiveResult(null);
    try {
      const j = await run(true);
      setDryResult(j);
      toast.success(`Dry run complete — ${j.updated} record(s) would be updated`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDryRunning(false);
    }
  };

  const doApply = async () => {
    if (!dryResult) return;
    if (!window.confirm(
      `Apply backfill to ${dryResult.updated} Airtable record(s)? This will write user_id and user_role to each matched row.`
    )) return;
    setLiveRunning(true);
    try {
      const j = await run(false);
      setLiveResult(j);
      toast.success(`Applied — ${j.updated} record(s) written to Airtable`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLiveRunning(false);
    }
  };

  const shown = liveResult || dryResult;
  const isDry = shown && !liveResult;

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-6 mt-6"
      data-testid="sa-airtable-backfill-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-indigo-700" />
          <h3 className="text-lg font-semibold text-slate-900">Airtable Allowlist Backfill</h3>
        </div>
        {shown && (
          <button
            onClick={() => { setDryResult(null); setLiveResult(null); }}
            className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
            data-testid="sa-airtable-backfill-reset"
          >
            <RefreshCw className="w-3 h-3" /> Reset
          </button>
        )}
      </div>

      <p className="text-xs text-slate-500 mb-4">
        Retroactively fill <code className="text-slate-700 bg-slate-100 px-1 rounded">user_id</code>
        {' '}and{' '}<code className="text-slate-700 bg-slate-100 px-1 rounded">user_role</code> on existing
        Airtable Allowlist rows by matching{' '}<code className="text-slate-700 bg-slate-100 px-1 rounded">villa_number</code>
        {' '}+{' '}<code className="text-slate-700 bg-slate-100 px-1 rounded">community_id</code> against the
        users collection. Always runs a dry run first so you can review the impact before writing.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        <Button
          onClick={doDryRun}
          disabled={dryRunning || liveRunning}
          data-testid="sa-airtable-backfill-dryrun"
          className="bg-slate-700 hover:bg-slate-800 text-white"
        >
          {dryRunning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1.5" />}
          Run dry preview
        </Button>
        <Button
          onClick={doApply}
          disabled={!dryResult || dryRunning || liveRunning || (dryResult.updated === 0)}
          data-testid="sa-airtable-backfill-apply"
          className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
        >
          {liveRunning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
          Apply {dryResult?.updated ? `(${dryResult.updated})` : ''}
        </Button>
      </div>

      {shown && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="sa-airtable-backfill-results">
          <div className="flex items-center gap-2 mb-2">
            <Badge className={`${isDry ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'} border-0 text-[10px]`}>
              {isDry ? 'DRY PREVIEW' : 'APPLIED'}
            </Badge>
            <span className="text-xs text-slate-500">
              uid_field=<code>{shown.uid_field || '—'}</code>
              {' '}· role_field=<code>{shown.role_field || '—'}</code>
            </span>
          </div>
          <ResultRow label="Scanned"               value={shown.scanned}               tone="slate" />
          <ResultRow label={isDry ? 'Would update' : 'Updated'} value={shown.updated} tone={shown.updated ? 'green' : 'slate'} />
          <ResultRow label="Skipped (already set)" value={shown.skipped_already_set}   tone="blue" />
          <ResultRow label="Skipped (no match in users)" value={shown.skipped_no_match} tone="amber" />
          <ResultRow label="Errors"                value={shown.errors}                tone={shown.errors ? 'red' : 'slate'} />

          {shown.samples?.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-semibold text-slate-600 mb-1.5">Sample records (first {shown.samples.length}):</div>
              <div className="space-y-1 max-h-40 overflow-y-auto text-[11px] font-mono bg-white rounded border border-slate-200 p-2">
                {shown.samples.map((s, i) => (
                  <div key={i} className="text-slate-700 truncate">
                    villa=<span className="text-slate-900 font-semibold">{s.villa || '—'}</span>
                    {' '}→ uid=<span className="text-emerald-700">{(s.user_id || '').slice(0, 8) || '—'}</span>
                    {' '}role=<span className="text-blue-700">{s.user_role || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isDry && shown.updated > 0 && (
            <div className="mt-3 flex items-start gap-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              Dry run complete. Click <strong>Apply</strong> above to write these changes to Airtable.
            </div>
          )}
          {isDry && shown.updated === 0 && (
            <div className="mt-3 flex items-start gap-2 p-2 rounded bg-slate-100 border border-slate-200 text-xs text-slate-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              No records need updating. Either all rows are already filled, or there are no matching users in MongoDB for the remaining villas.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SAAirtableBackfillPanel;
